import { useQuery } from "@tanstack/react-query";
import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { DynamicApplicationForm, FormSchema } from "@/components/DynamicApplicationForm";
import { Link } from "wouter";
import { Trophy, ArrowLeft, Loader2 } from "lucide-react";

import { ATHLETE_SCHEMA_DEFAULT } from "@/lib/defaultFormSchemas";

export default function JoinAthlete() {
  const { data: schema, isLoading } = useQuery<FormSchema>({
    queryKey: ["/api/join/schema/athlete"],
    queryFn: async () => {
      try {
        const r = await fetch("/api/join/schema/athlete");
        if (!r.ok) return ATHLETE_SCHEMA_DEFAULT;
        const data = await r.json();
        if (!data || !data.sections) return ATHLETE_SCHEMA_DEFAULT;
        return data;
      } catch (err) {
        return ATHLETE_SCHEMA_DEFAULT;
      }
    },
    staleTime: 1000 * 60 * 5,
    initialData: ATHLETE_SCHEMA_DEFAULT,
  });

  return (
    <>
      <SeoHead
        title="Athlete Application"
        description="Apply to join Eliada Sports Agency as a professional athlete. Submit your athlete representation application and take the next step in your career."
        path="/join/athlete"
      />
      <PageTransition className="pt-24 pb-24 bg-[#070b13] min-h-screen">
        {/* Header */}
        <div className="relative py-16 border-b border-white/5 bg-secondary/20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent opacity-70" />
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link href="/join" className="inline-flex items-center gap-2 text-xs text-white/40 hover:text-primary transition-colors mb-6">
              <ArrowLeft size={14} /> Back to Pathways
            </Link>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                <Trophy size={28} className="text-primary" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-primary mb-1">Athlete Application</p>
                <h1 className="text-3xl sm:text-4xl font-display font-extrabold text-white">
                  ATHLETE REPRESENTATION<br className="hidden sm:block" /> APPLICATION FORM
                </h1>
              </div>
            </div>
            <p className="text-white/50 text-sm mt-4 max-w-2xl leading-relaxed">
              Complete all sections below. Fields marked with <span className="text-primary font-bold">*</span> are required.
              Applications are reviewed within <strong className="text-white/70">5–7 business days</strong>.
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
          {!schema || !schema.sections ? (
            <div className="text-center py-24 text-white/50">
              <p>Unable to load the application form. Please try again later.</p>
            </div>
          ) : (
            <DynamicApplicationForm schema={schema} formType="athlete" />
          )}
        </div>
      </PageTransition>
    </>
  );
}
